export interface DebugLogEntry {
    timestamp: number;
    level: "log" | "warn" | "error" | "info";
    message: string;
}

interface RawEntry {
    t: number;
    l: DebugLogEntry["level"];
    m: string;
}

declare global {
    interface Window {
        __dbgPush?: (level: string, args: unknown[]) => void;
        __dbgRead?: () => RawEntry[];
        __dbgClear?: () => void;
        __dbgShow?: () => void;
        __dbgShowFallback?: () => void;
    }
}

let installed = false;

function readEntries(): DebugLogEntry[] {
    const raw = window.__dbgRead?.() ?? [];
    return raw.map((e) => ({
        timestamp: e.t,
        level: e.l,
        message: e.m,
    }));
}

function formatTime(ts: number): string {
    return new Date(ts).toISOString().slice(11, 23);
}

const LEVEL_COLORS: Record<DebugLogEntry["level"], string> = {
    error: "#ff6b6b",
    warn: "#ffa94d",
    info: "#74c0fc",
    log: "rgba(255,255,255,0.6)",
};

function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildOverlayHTML(entries: DebugLogEntry[]): string {
    const lines = entries
        .map((e) => {
            const color = LEVEL_COLORS[e.level];
            const time = formatTime(e.timestamp);
            const escaped = escapeHtml(e.message);
            return (
                `<div style="padding:3px 0;border-bottom:1px solid rgba(255,255,255,0.04);display:flex;gap:6px;line-height:1.4">` +
                `<span style="color:rgba(255,255,255,0.3);white-space:nowrap;flex-shrink:0">${time}</span>` +
                `<span style="color:${color};width:36px;flex-shrink:0;text-align:right;text-transform:uppercase">${e.level}</span>` +
                `<span style="color:${color};word-break:break-all;white-space:pre-wrap">${escaped}</span>` +
                `</div>`
            );
        })
        .join("");

    return `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:14px 16px 10px;border-bottom:1px solid rgba(255,255,255,0.1)">
            <span style="font-size:15px;font-weight:700;color:#fff">Debug Log (${entries.length})</span>
            <div style="display:flex;gap:8px">
                <button id="__dbg_copy" style="background:rgba(255,255,255,0.1);border:none;border-radius:8px;color:#fff;font-size:12px;padding:6px 12px;cursor:pointer">Copy</button>
                <button id="__dbg_clear" style="background:rgba(255,255,255,0.1);border:none;border-radius:8px;color:#fff;font-size:12px;padding:6px 12px;cursor:pointer">Clear</button>
                <button id="__dbg_close" style="background:rgba(255,60,60,0.2);border:none;border-radius:8px;color:#ff6b6b;font-size:12px;font-weight:700;padding:6px 12px;cursor:pointer">✕</button>
            </div>
        </div>
        <div id="__dbg_scroll" style="flex:1;overflow-y:auto;padding:8px 12px;-webkit-overflow-scrolling:touch;font-family:'SF Mono','Menlo','Monaco','Courier New',monospace;font-size:10.5px">
            ${lines || '<div style="color:rgba(255,255,255,0.3);text-align:center;padding:32px 16px;font-size:12px">No log entries</div>'}
        </div>
        <div style="padding:8px 16px;padding-bottom:calc(8px + env(safe-area-inset-bottom));border-top:1px solid rgba(255,255,255,0.08)">
            <span style="color:rgba(255,255,255,0.3);font-size:11px">${entries.length} entries · Tap screen 10× to toggle</span>
        </div>
    `;
}

function showOverlay() {
    const existing = document.getElementById("__dbg_overlay");
    if (existing) {
        existing.remove();
        return;
    }

    const entries = readEntries();
    const overlay = document.createElement("div");
    overlay.id = "__dbg_overlay";
    overlay.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:999999",
        "background:#1a1a2e",
        "display:flex",
        "flex-direction:column",
        "padding-top:env(safe-area-inset-top)",
    ].join(";");
    overlay.innerHTML = buildOverlayHTML(entries);
    document.body.appendChild(overlay);

    const scrollContainer = document.getElementById("__dbg_scroll");
    if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }

    document.getElementById("__dbg_close")?.addEventListener("click", () => {
        overlay.remove();
    });

    document.getElementById("__dbg_clear")?.addEventListener("click", () => {
        window.__dbgClear?.();
        overlay.remove();
    });

    document.getElementById("__dbg_copy")?.addEventListener("click", () => {
        const text = entries
            .map(
                (e) =>
                    `[${formatTime(e.timestamp)}] [${e.level.toUpperCase()}] ${e.message}`,
            )
            .join("\n");
        navigator.clipboard.writeText(text).then(() => {
            const btn = document.getElementById("__dbg_copy");
            if (btn) {
                btn.textContent = "Copied!";
                setTimeout(() => {
                    btn.textContent = "Copy";
                }, 1500);
            }
        });
    });
}

export function installDebugLog() {
    if (installed) {
        return;
    }
    installed = true;

    window.__dbgShow = showOverlay;

    if (window.__dbgPush) {
        window.__dbgPush("info", ["[main.ts] installDebugLog() running"]);
    }
}

export function logStep(message: string) {
    if (window.__dbgPush) {
        window.__dbgPush("info", [`[step] ${message}`]);
    }
}

export function useDebugLog() {
    function clear() {
        window.__dbgClear?.();
    }
    function show() {
        showOverlay();
    }
    return {
        entries: readEntries,
        clear,
        show,
    };
}
